import { Delete } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import TextTruncate from 'react-text-truncate';

import { theme as evoachDefaultTheme } from '../../theme/theme';
import { BaseComponent } from '../Component';
import { EvoachLinkify } from '../EvoachLinkify';
import { CardImageEnum, CardImageSize, CardSize } from '../ObjectCard';
import { DisplayTextProps } from '../VerticalTimeline';

import AdditionalResourceList, {
  AdditionalResource,
  renderURLIcon,
} from './AdditionalResourceList';
import { URLIconEnum } from './URLIconEnum';

export interface ObjectCardHorizontalComponent extends BaseComponent {
  type: 'objectCard';
  props: HorizontalObjectCardProps;
}
export interface HorizontalObjectCardProps {
  title: string;
  description: string;
  duration: string;
  id?: string;
  image?: CardImageEnum;
  imageSrc?: string;
  imageSize?: CardImageSize;
  cardSize?: CardSize;
  url?: string;
  icon?: URLIconEnum;
  buttonDescription?: string;
  selected?: boolean;
  displayTexts?: DisplayTextProps;
  additionalResources?: AdditionalResource[];
  actionCallback?: () => void;
  deleteCallback?: () => void;
  duplicateCallback?: () => void;
}

export const ObjectCardHorizontal: React.FC<HorizontalObjectCardProps> = ({
  title,
  description,
  duration,
  id,
  image,
  imageSrc,
  imageSize = CardImageSize.LARGE,
  cardSize = CardSize.LARGE,
  url,
  icon,
  selected,
  displayTexts,
  buttonDescription,
  additionalResources,
  actionCallback,
  deleteCallback,
  duplicateCallback,
}) => {
  const [showAdditionalResources, setShowAdditionalResources] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const displayDescription = cardSize == CardSize.MINI ? null : description;

  const cardImageSize =
    cardSize == CardSize.MINI ? CardImageSize.MINI : imageSize;
  const baseWidth = cardSize * 1.8;
  // const descriptionWidth = cardSize === CardSize.LARGE ? 110 : 55;
  const width =
    baseWidth - 145 + (image || imageSrc ? cardImageSize : 0) + 'px';

  let height = cardSize === CardSize.LARGE ? '255px' : '210px';

  const parsedImageUrl = imageSrc ? `url(${imageSrc})` : undefined;

  switch (cardSize) {
    case CardSize.LARGE:
      height = '260px';
      break;
    case CardSize.SMALL:
      height = '210px';
      break;
    case CardSize.MINI:
      height = '130px';
      break;
  }

  return (
    <Card
      sx={{
        width: width,
        maxWidth: width,
        maxHeight:
          showAdditionalResources || showFullDescription ? '700px' : height,
        overflow: 'hidden',
        display: 'flex',
        margin: '2px',
        borderRadius: 3,
        borderColor: selected
          ? evoachDefaultTheme.palette.primary.main
          : undefined,
        boxShadow: selected
          ? `0 0 10px ${evoachDefaultTheme.palette.primary.main}`
          : undefined,
        backgroundColor: '#F5F5F5',
      }}
      id={id + ''}
      raised
    >
      <Box sx={{ flexDirection: 'row', display: 'flex', width: '100%' }}>
        <Box
          style={{
            backgroundImage: parsedImageUrl ?? undefined,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            flex: '0 0 150px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundSize: 'cover',
          }}
        />

        <Box style={{ flex: 2, display: 'flex', flexDirection: 'row' }}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              justifyContent: 'space-between',
              paddingTop: '1em',
              position: 'relative',
              backgroundColor: 'white',
            }}
          >
            {deleteCallback ? (
              <Box
                style={{
                  cursor: 'pointer',
                  marginLeft: 5,
                  position: 'absolute',
                  right: 10,
                  top: 10,
                }}
                onClick={() => deleteCallback()}
                title="delete"
              >
                <Delete
                  style={{ color: evoachDefaultTheme.palette.secondary.main }}
                />
              </Box>
            ) : (
              <Box style={{ position: 'absolute', top: 15, right: 15 }}>
                <Typography
                  sx={{
                    color: evoachDefaultTheme.palette.secondary.main,
                  }}
                >
                  {renderURLIcon(icon, 'large')}
                </Typography>
              </Box>
            )}
            {duplicateCallback ? (
              <Box
                style={{
                  cursor: 'pointer',
                  marginLeft: 5,
                  position: 'absolute',
                  right: 10,
                  bottom: 10,
                }}
                onClick={() => duplicateCallback()}
                title="Duplicate"
              >
                <ContentCopyIcon
                  style={{ color: evoachDefaultTheme.palette.secondary.main }}
                />
              </Box>
            ) : null}
            <Typography
              variant="subtitle1"
              fontWeight={600}
              lineHeight="normal"
              component="div"
              sx={{
                overflow: 'hidden',
                color: evoachDefaultTheme.palette.primary.contrastText,
                marginBottom: '0.5em',
                paddingRight: '1em',
              }}
            >
              <TextTruncate
                line={cardSize === CardSize.MINI ? 2 : 10}
                element="span"
                truncateText="…"
                text={title ?? ''}
              />{' '}
            </Typography>

            <Box
              sx={{
                overflow: 'auto',
                marginBottom: '1em',
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                sx={{
                  color: evoachDefaultTheme.palette.primary.contrastText,
                  whiteSpace: 'pre-wrap',
                  width: '90%',
                }}
              >
                {description.length < 250 || showFullDescription ? (
                  <>
                    <EvoachLinkify>{displayDescription}</EvoachLinkify>
                    {showFullDescription && (
                      <Link
                        color={evoachDefaultTheme.palette.secondary.main}
                        onClick={() =>
                          setShowFullDescription(!showFullDescription)
                        }
                        sx={{ cursor: 'pointer' }}
                      >
                        <br />
                        <br />
                        {displayTexts?.showLess}
                      </Link>
                    )}
                  </>
                ) : (
                  <TextTruncate
                    line={1}
                    element="span"
                    truncateText="…"
                    text={displayDescription ?? ''}
                    textTruncateChild={
                      <Link
                        color={evoachDefaultTheme.palette.secondary.main}
                        onClick={() =>
                          setShowFullDescription(!showFullDescription)
                        }
                        sx={{ cursor: 'pointer' }}
                      >
                        {displayTexts?.readOn}
                      </Link>
                    }
                  />
                )}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              fontSize={13}
              fontWeight={500}
              component="div"
              style={{
                width: '100%',
                overflow: 'hidden',
                color: evoachDefaultTheme.palette.primary.contrastText,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center',
                }}
              >
                {duration !== undefined && duration !== '' ? (
                  <>
                    <WatchLaterIcon fontSize="inherit" color="inherit" />
                    <span
                      style={{
                        paddingLeft: '10px',
                      }}
                    >
                      {duration}
                    </span>
                  </>
                ) : (
                  ' '
                )}
              </Box>
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginTop:
                  additionalResources !== undefined &&
                  additionalResources.length > 0
                    ? '1em'
                    : '0px',
              }}
            >
              {additionalResources !== undefined &&
                additionalResources.length > 0 && (
                  <>
                    <Box>
                      <IconButton
                        sx={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0 }}
                        onClick={() =>
                          setShowAdditionalResources(!showAdditionalResources)
                        }
                      >
                        {showAdditionalResources ? (
                          <ArrowDropUpIcon />
                        ) : (
                          <ArrowDropDownIcon />
                        )}
                        <Link
                          color="secondary"
                          variant="body2"
                          underline="none"
                        >
                          {displayTexts?.additionalResourceText}
                        </Link>
                      </IconButton>
                      {showAdditionalResources && (
                        <AdditionalResourceList
                          resources={additionalResources}
                        />
                      )}
                    </Box>
                  </>
                )}
              {url || actionCallback ? (
                <Button
                  sx={{
                    height: '2em',
                    position:
                      additionalResources === undefined ||
                      additionalResources.length === 0
                        ? 'absolute'
                        : 'static',
                    bottom: '2em',
                    right: '1em',
                  }}
                  onClick={() => {
                    if (actionCallback) {
                      actionCallback();
                    } else {
                      if (url) {
                        const newurl =
                          url.startsWith('https://') ||
                          url.startsWith('http://')
                            ? url
                            : 'https://' + url;
                        window.open(newurl, '_blank');
                      }
                    }
                  }}
                >
                  <Tooltip
                    arrow
                    placement="top"
                    title={buttonDescription ?? ''}
                  >
                    <Typography variant="body2">Start</Typography>
                  </Tooltip>
                </Button>
              ) : null}
            </Box>
          </CardContent>
        </Box>
      </Box>
    </Card>
  );
};
